import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../services/firebase';
import AIPiggyBank from '../components/AIPiggyBank';

type PageState = 'loading' | 'form' | 'success' | 'error';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setPageState('error');
        setErrorMessage('Invalid password reset link. No reset code found.');
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setPageState('form');
      } catch (err: unknown) {
        setPageState('error');
        if (err instanceof Error) {
          if (err.message.includes('invalid-action-code')) {
            setErrorMessage('This password reset link is invalid or has already been used.');
          } else if (err.message.includes('expired-action-code')) {
            setErrorMessage('This password reset link has expired. Please request a new one.');
          } else {
            setErrorMessage('Unable to verify reset link. The link may have expired or already been used.');
          }
        } else {
          setErrorMessage('An unexpected error occurred. Please try again.');
        }
      }
    };

    verifyCode();
  }, [oobCode]);

  const validateForm = (): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!oobCode) {
      setFormError('Invalid reset code. Please request a new password reset link.');
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPageState('success');
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes('weak-password')) {
          setFormError('Password is too weak. Please choose a stronger password.');
        } else if (err.message.includes('expired-action-code')) {
          setFormError('This reset link has expired. Please request a new one.');
        } else if (err.message.includes('invalid-action-code')) {
          setFormError('This reset link is no longer valid. Please request a new one.');
        } else {
          setFormError('Failed to reset password. Please try again.');
        }
      } else {
        setFormError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const PasswordVisibilityToggle = ({
    show,
    onToggle,
  }: {
    show: boolean;
    onToggle: () => void;
  }) => (
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
    >
      {show ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
          />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      )}
    </button>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #120938 0%, #241A4C 100%)' }}
    >
      <AIPiggyBank className="absolute bottom-0 right-0 w-96 h-96 opacity-25 pointer-events-none select-none transform translate-x-16 translate-y-16" />

      <div className="bg-bg-primary p-8 rounded-2xl shadow-xl w-full max-w-md relative z-10">
        {/* App Logo */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-teal to-brand-indigo rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
        </div>

        {pageState === 'loading' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <svg className="animate-spin h-12 w-12 text-brand-teal" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Verifying reset link...</h1>
            <p className="text-text-secondary">Please wait while we verify your password reset link.</p>
          </div>
        )}

        {pageState === 'form' && (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-text-primary">Reset Your Password</h1>
              <p className="text-text-secondary mt-2">
                Enter a new password for <span className="font-medium text-text-primary">{email}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {formError && (
                <div className="bg-system-error/10 border border-system-error/30 text-system-error px-4 py-3 rounded-xl text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 bg-bg-secondary border border-border-primary rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal outline-none transition-all text-text-primary placeholder-text-tertiary"
                    placeholder="Enter new password"
                  />
                  <PasswordVisibilityToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                </div>
                <p className="text-xs text-text-tertiary mt-1">Must be at least 8 characters</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 bg-bg-secondary border border-border-primary rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal outline-none transition-all text-text-primary placeholder-text-tertiary"
                    placeholder="Confirm new password"
                  />
                  <PasswordVisibilityToggle
                    show={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-brand-teal to-brand-indigo text-white py-3 px-4 rounded-xl hover:opacity-90 focus:ring-4 focus:ring-brand-teal/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        )}

        {pageState === 'success' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-system-success/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Password Updated!</h1>
            <p className="text-text-secondary">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <div className="pt-4">
              <p className="text-sm text-text-tertiary">You may now close this page and return to the app.</p>
            </div>
          </div>
        )}

        {pageState === 'error' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-system-error/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Reset Link Invalid</h1>
            <p className="text-text-secondary">{errorMessage}</p>
            <div className="pt-4 space-y-3">
              <p className="text-sm text-text-tertiary">
                Need to reset your password? Open the app and request a new password reset link.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
